import { AxiosError } from 'axios';
import { getErrorObject } from '@/helpers/http.helpers';
import { AppError } from '@/types/error';
import { showErrorToast } from '@/helpers/toast.helpers';

interface ErrorHandlerOptions {
  showToast?: boolean;
  customMessage?: string;
  onError?: (error: AppError) => void;
}

/**
 * Centralized error handler for API calls
 * Automatically shows toast notifications for errors
 * @param error - The Axios error object
 * @param options - Optional configuration for error handling
 */
export const handleApiError = (
  error: AxiosError,
  options: ErrorHandlerOptions = {}
): AppError => {
  const { showToast = true, customMessage, onError } = options;

  // Process error using existing helper
  const processedError = getErrorObject(error);

  // Show toast notification if enabled
  if (showToast) {
    const errorTitle = processedError.title || 'Error';
    const errorMessage = customMessage || processedError.message || 'An unexpected error occurred';

    // Standardized grain-styled error toast (see @/helpers/toast.helpers).
    showErrorToast(errorTitle, errorMessage);
  }

  // Call custom error handler if provided
  if (onError) {
    onError(processedError);
  }

  return processedError;
};

export const handleFeedbackApiError = (
  error: AxiosError | undefined,
  chatId?: string,
  title: string = 'Something went wrong. Please try again.'
): void => {
  if (error?.response?.status === 404) {
    console.warn(
      `[Feedback] Endpoint not found — check API route configuration. Chat ID: ${chatId ?? 'unknown'}`
    );
  }
  showErrorToast(title);
};

/**
 * Check if error should be handled globally
 * Some errors might need custom handling in specific components
 */
export const shouldHandleGlobally = (error: AxiosError): boolean => {
  // Skip global handling for requests that opted out via config flag
  if (error.config?.skipGlobalErrorHandling) {
    return false;
  }

  // Skip global handling for specific status codes if needed
  const skipStatusCodes = [401]; // Add status codes to skip global handling
  
  if (error.response?.status && skipStatusCodes.includes(error.response.status)) {
    return false;
  }

  return true;
};
